import ActivityLogic from '../logic/ActivityLogic'
import ChallengeBucket from '../model/ChallengeBucket'
import { resolveActivityIconUrl } from '../config/publicIcons.config'
import Activity from '../model/Activity'
import {
  bucketDisplayLabel,
  type BucketBreakdown,
  type NamedBucketBreakdown,
  toNamedBreakdown,
} from '../logic/bucketUtils'

export function getBucketDisplayInfo(bucket: ChallengeBucket) {
  return {
    icon: resolveActivityIconUrl(bucket.icon),
    label: bucket.name,
  }
}

export function getBucketBreakdown(
  activityBreakdownByActivityId: { [activityId: string]: number },
  activities: Activity[],
  buckets: ChallengeBucket[],
): NamedBucketBreakdown {
  const byId: BucketBreakdown = {}
  for (const bucket of buckets) {
    byId[bucket.id] = 0
  }

  const activityById = new Map(activities.map((activity) => [activity.id, activity]))
  for (const [activityId, amount] of Object.entries(activityBreakdownByActivityId)) {
    const activity = activityById.get(activityId)
    const bucket = buckets.find((candidate) => activity && ActivityLogic.activityFeedsBucket(activity, candidate))
    if (!bucket) continue
    byId[bucket.id] = (byId[bucket.id] ?? 0) + amount
  }

  return toNamedBreakdown(buckets, byId)
}

interface GoalProgress {
  label: string
  amount: number
  threshold: number
  isGoalMet: boolean
}

export type RequiredActivityProgress =
  | (GoalProgress & { kind: 'bucket'; icon: string })
  | (GoalProgress & { kind: 'restDays' })

export function formatRequiredActivityBreakdown(
  buckets: ChallengeBucket[],
  bucketBreakdown: NamedBucketBreakdown,
  restDayProgress: { count: number; target: number },
): RequiredActivityProgress[] {
  const bucketProgress = buckets.map((bucket): RequiredActivityProgress => {
    const amount = bucketBreakdown[bucket.name] ?? 0
    const { icon, label } = getBucketDisplayInfo(bucket)
    return {
      kind: 'bucket',
      icon,
      amount: Math.round(amount),
      threshold: bucket.goal,
      label,
      isGoalMet: amount >= bucket.goal,
    }
  })

  if (restDayProgress.target <= 0) return bucketProgress

  const restDays = Math.round(restDayProgress.count)
  const restDayTarget = Math.round(restDayProgress.target)

  return [
    ...bucketProgress,
    {
      kind: 'restDays',
      amount: restDays,
      threshold: restDayTarget,
      label: 'Rest Days',
      isGoalMet: restDays >= restDayTarget,
    },
  ]
}

export { bucketDisplayLabel }
